import { getEventType, EVENT_STATUS_LABELS } from '../constants/eventTypes';
import { DEFAULT_RSVP_FIELDS, normalizeRsvpFields } from '../constants/rsvpFields';

export const TYPE_THEMES = {
  ATTENDANCE: {
    gradient: 'from-emerald-500 via-teal-500 to-cyan-500',
    ring: 'ring-emerald-400/30',
    badge: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
    glow: 'shadow-emerald-500/20',
  },
  SPLITWISE: {
    gradient: 'from-amber-500 via-orange-500 to-yellow-500',
    ring: 'ring-amber-400/30',
    badge: 'bg-amber-500/15 text-amber-800 dark:text-amber-200',
    glow: 'shadow-amber-500/20',
  },
  EXPENSE_TRACKING: {
    gradient: 'from-orange-500 via-amber-500 to-yellow-400',
    ring: 'ring-orange-400/30',
    badge: 'bg-orange-500/15 text-orange-800 dark:text-orange-200',
    glow: 'shadow-orange-500/20',
  },
  CALCULATION: {
    gradient: 'from-violet-500 via-purple-500 to-fuchsia-500',
    ring: 'ring-violet-400/30',
    badge: 'bg-violet-500/15 text-violet-700 dark:text-violet-300',
    glow: 'shadow-violet-500/20',
  },
  POLL: {
    gradient: 'from-blue-500 via-indigo-500 to-sky-500',
    ring: 'ring-blue-400/30',
    badge: 'bg-blue-500/15 text-blue-700 dark:text-blue-300',
    glow: 'shadow-blue-500/20',
  },
  ACTION_TRACKING: {
    gradient: 'from-teal-400 via-emerald-500 to-cyan-500',
    ring: 'ring-teal-400/30',
    badge: 'bg-teal-500/15 text-teal-800 dark:text-teal-200',
    glow: 'shadow-teal-500/20',
  },
  TASK: {
    gradient: 'from-indigo-500 via-blue-600 to-violet-600',
    ring: 'ring-indigo-400/30',
    badge: 'bg-indigo-500/15 text-indigo-700 dark:text-indigo-300',
    glow: 'shadow-indigo-500/20',
  },
  MEETING: {
    gradient: 'from-sky-500 via-cyan-500 to-teal-500',
    ring: 'ring-sky-400/30',
    badge: 'bg-sky-500/15 text-sky-700 dark:text-sky-300',
    glow: 'shadow-sky-500/20',
  },
  CUSTOM: {
    gradient: 'from-slate-500 via-gray-600 to-zinc-600',
    ring: 'ring-slate-400/30',
    badge: 'bg-slate-500/15 text-slate-700 dark:text-slate-300',
    glow: 'shadow-slate-500/20',
  },
};

export const getTypeTheme = (eventType) => TYPE_THEMES[eventType] || TYPE_THEMES.MEETING;

export const formatTime12h = (timeStr) => {
  if (!timeStr) return '';
  const match = String(timeStr).trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return timeStr;
  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const meridiem = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${hours}:${minutes} ${meridiem}`;
};

export const formatEventSchedule = (info) => {
  if (!info) return '';
  const { startDate, startTime, endTime, allDay } = info;
  if (!startDate) return 'Schedule TBD';
  if (allDay) {
    try {
      const [y, m, d] = startDate.split('-').map(Number);
      const date = new Date(y, m - 1, d);
      return `${date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })} · All day`;
    } catch {
      return `${startDate} · All day`;
    }
  }
  try {
    const [y, m, d] = startDate.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    const day = date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
    const start = formatTime12h(startTime);
    const end = formatTime12h(endTime);
    return `${day} · ${start}${end ? ` – ${end}` : ''}`;
  } catch {
    const start = formatTime12h(startTime);
    const end = formatTime12h(endTime);
    return `${startDate} · ${start}${end ? ` – ${end}` : ''}`;
  }
};

export const resolveEventLocation = (info) => {
  if (!info) return { label: '', link: '' };
  const label = info.locationLabel || '';
  const link = info.locationLink || '';
  return { label, link };
};

export const resolveLocationFromDto = (location = {}) => {
  const parts = [location.venue, location.address].filter(Boolean);
  const label = parts.join(', ') || location.label || location.name || '';
  const link = location.meetingUrl || location.mapUrl || location.link || location.url || '';
  return { label, link };
};

export const eventDtoToTimelineInfo = (dto) => {
  if (!dto?.id) return null;
  const schedule = dto.schedule || {};
  const location = dto.location || {};
  const { label: locationLabel, link: locationLink } = resolveLocationFromDto(location);
  const pollVotesByUserId = { ...(dto.pollVotesByUserId || {}) };
  const participants = (dto.participants || []).map((p) => {
    const selectedPollOptions = Array.isArray(p.selectedPollOptions)
      ? p.selectedPollOptions
      : (Array.isArray(p.responseDetails?.selectedOptions) ? p.responseDetails.selectedOptions : []);
    if (p.userId && selectedPollOptions.length && !pollVotesByUserId[p.userId]) {
      pollVotesByUserId[p.userId] = selectedPollOptions;
    }
    return {
      userId: p.userId,
      userName: p.userName,
      profilePicture: p.profilePicture || null,
      role: p.role,
      status: p.status,
      responseDetails: p.responseDetails || null,
      selectedPollOptions,
    };
  });
  return {
    eventId: dto.id,
    name: dto.name,
    description: dto.description,
    eventType: dto.eventType,
    category: dto.category,
    status: dto.status,
    creatorId: dto.creatorId,
    creatorName: dto.creatorName,
    startDate: schedule.startDate,
    startTime: schedule.startTime,
    endDate: schedule.endDate,
    endTime: schedule.endTime,
    timezone: schedule.timezone,
    allDay: Boolean(schedule.allDay),
    allowInviteOthers: Boolean(
      schedule.allowInviteOthers
      || dto.permissions?.canInviteOthers
    ),
    responseType: dto.responsePolicy?.type || 'NONE',
    rsvpFields: normalizeRsvpFields(dto.responsePolicy?.rsvpFields ?? dto.rsvpFields),
    locationLabel,
    locationLink,
    tags: dto.tags || [],
    participants,
    attachments: Array.isArray(dto.attachments) ? dto.attachments : [],
    configuration: dto.configuration || {},
    expenseSummary: dto.expenseSummary || dto.configuration?.expenseSummary || null,
    pollQuestion: dto.responsePolicy?.pollQuestion || dto.configuration?.question || dto.pollQuestion || dto.name || '',
    pollOptions: dto.responsePolicy?.pollOptions || dto.configuration?.options || dto.pollOptions || [],
    allowMultiple: Boolean(dto.responsePolicy?.allowMultiple || dto.configuration?.allowMultiple || dto.allowMultiple),
    pollVotesByUserId,
  };
};

export const isEventImageAttachment = (attachment) => {
  if (!attachment) return false;
  const type = String(attachment.fileType || '').toUpperCase();
  if (type === 'IMAGE' || type.startsWith('IMAGE/')) return true;
  const name = String(attachment.fileName || attachment.fileUrl || '').toLowerCase();
  return /\.(png|jpe?g|gif|webp|bmp|svg|heic|heif)$/i.test(name);
};

export const splitEventAttachments = (attachments = []) => {
  const list = Array.isArray(attachments) ? attachments : [];
  const images = list.filter(isEventImageAttachment);
  const files = list.filter((a) => !isEventImageAttachment(a));
  return { images, files };
};

export const getStatusLabel = (status) => EVENT_STATUS_LABELS[status] || status || 'Event';

export const getTypeMeta = (eventType) => {
  const type = getEventType(eventType);
  return {
    icon: type?.icon || '📅',
    label: type?.label || 'Event',
  };
};

export const PARTICIPANT_STATUS_BADGE = {
  ACCEPTED: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  INVITED: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  PENDING: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  DECLINED: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200',
  TENTATIVE: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
};

export const PARTICIPANT_STATUS_LABEL = {
  ACCEPTED: 'Accepted',
  INVITED: 'Invited',
  PENDING: 'Pending',
  DECLINED: 'Declined',
  TENTATIVE: 'Tentative',
};

export const formatRsvpSummary = (responseDetails) => {
  if (!responseDetails) return '';
  const parts = [];
  if (responseDetails.guestCount != null) {
    parts.push(`${responseDetails.guestCount} attending`);
  }
  const guests = Array.isArray(responseDetails.guests) ? responseDetails.guests : [];
  if (guests.length > 0) {
    const names = guests.map((g) => g.name).filter(Boolean);
    if (names.length) parts.push(names.join(', '));
  }
  const customAnswers = Array.isArray(responseDetails.customAnswers) ? responseDetails.customAnswers : [];
  customAnswers.forEach((item) => {
    const value = String(item?.value || '').trim();
    if (!value) return;
    const label = String(item?.label || '').trim();
    parts.push(label ? `${label}: ${value}` : value);
  });
  return parts.join(' · ');
};
